import React, { lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import GlobalToast from '../global-toast.coffee'
import {UIRouter, UIView, hashLocationPlugin} from '@uirouter/react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from '../theme.coffee'
import moment from 'moment'
import TitleBar from './title_bar.coffee'
import Application from '../loopback_api.coffee'
import ErrorBoundary from '../error_boundary.coffee'
import {isUnauthenticatedDeepLink} from '../Login/unauthenticated_routes.coffee'
import { LoadingScreen as Loading, hide_agent_widget } from '../common.coffee'
import axios from 'axios'
import ClientAPI from  "../../../../../schema/framework/shared/client_api.coffee"
import {i18nCreateElement, initI18n, TranslationProvider, I18nRegistry, t} from '../../../../../schema/framework/shared/i18n/index.coffee'

# Normalize route by removing student IDs but keeping game IDs
# This is el-feet specific routing logic
normalizeRoute = (route) ->
  return route unless route?
  # Remove studentId from /play/:gameId/:studentId routes
  # Keep the gameId since each game has different strings
  route.replace(/^(#?\/play\/[0-9a-fA-F]{24})\/[0-9a-fA-F]{24}/, '$1')

standardComponent = (importPromise) ->
  LazyComponent = lazy(() => importPromise)
  (props) ->
    <Suspense fallback={<Loading/>}>
      <LazyComponent {...props} />
    </Suspense>

Assignment         = standardComponent import('../Assignment/assignment.coffee')
Cancel             = standardComponent import('../CancellationSurvey/new_cancellation_survey.coffee')
ClassroomFund      = standardComponent import('./classroom_fund.coffee')
Help               = standardComponent import('../Help/help.coffee')
# Institutional sign-in, NOT the parent screen. The parent flow emails a
# generated password on request, which is designed behaviour for a parent on a
# child's device and the wrong thing entirely for a login holding roster and
# student data. Card 6a639e22893d0bbbaaacec81.
Login              = standardComponent import('../Login/staff_login.coffee')
ResetPassword      = standardComponent import('../Login/reset_password.coffee')
Dashboard          = standardComponent import('../Dashboard/dashboard.coffee')
LongMilestoneReport = standardComponent import('../MilestoneReport/long_milestone_report.coffee')
ManageAccount      = standardComponent import('../ManageAccount/account.coffee')
TeacherDashboard   = standardComponent import('./teacher.coffee')
TeacherOnboarding  = standardComponent import('./onboarding.coffee')
UpdateScholarship  = standardComponent import('../UpdateScholarship/update_scholarship.coffee')
GameLayout         = standardComponent import('../Game/game_layout.coffee')
StudentApplication = standardComponent import('../StudentApplication/application.coffee')
StudentReport      = standardComponent import('../StudentReport/student_report.coffee')
GamesList          = standardComponent import('../GameList/games.coffee')
SubjectList        = standardComponent import('../SubjectList/subjects.coffee')
Tokens             = standardComponent import('../Tokens/tokens.coffee')

requireLogin = (trans, state) ->
  unless window.app?.loopbackAPI?.isLoggedIn()
    console.log "Redirecting the user to the login page..."
    trans.router.stateService.go 'login'

globalStates = [
  # Deliberately NOT behind requireLogin — someone redeeming a reset link
  # cannot sign in, which is the entire point. Signed-in users reach the same
  # screen from Manage Account, where it asks for the current password.
  name: 'reset',
  url: '/reset',
  component: ResetPassword,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Reset Password")
  ]
,
  name: 'login',
  url: '',
  component: Login,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Login")
  ]
,
  name: 'application',
  url:'/application',
  onEnter: requireLogin
  component: StudentApplication,
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Application")
      if fbq?
        fbq('track', 'Lead')
  ]
,
  name: 'account'
  url:'/account?tab'
  onEnter: requireLogin
  component: ManageAccount
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Account")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'classroom_fund'
  url:'/classroom_fund'
  onEnter: requireLogin
  component: ClassroomFund
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Classroom Fund")
  ]
,
  name: 'help'
  url: '/help'
  params: {url: {type: 'string', value: null}}
  redirectTo: requireLogin
  component: Help
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Help")
  ]
,
  name: 'update_scholarship'
  url: '/update_scholarship?back'
  redirectTo: requireLogin
  component: UpdateScholarship
  resolve: [
    token: 'back'
    deps: ['$transition$']
    resolveFn: (trans) =>
      trans.params().back
  ,
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Update Scholarship")
  ]
,
  name: 'teacherDashboard'
  url:'/teacher_dashboard?showIntro&tab'
  onEnter: requireLogin
  component: TeacherDashboard
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.activeStudentIndex = null
      window.app.trackPageView("2: Teacher Dashboard")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'teacher_onboarding'
  url:'/onboarding'
  onEnter: requireLogin
  component: TeacherOnboarding
  resolve:[
    token: "none"
    resolveFn: ->
      window.app.trackPageView("2: Teacher Onboarding")
  ]
,
  name: 'long_milestone_report'
  url: '/long_milestone_report?studentId'
  redirectTo: requireLogin
  component: LongMilestoneReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Long Placement Exam Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'assignment'
  url:'/assignment'
  onEnter: requireLogin
  component: Assignment
  resolve:[
    token: "assignments",
    resolveFn: ->
      window.app.trackPageView("2: Assignment")
      window.app.loopbackAPI.Assignment.find({})
  ]
,
  name: 'dashboard'
  url: '/dashboard?studentId'
  # dynamic: the dashboard writes the selected child here as the teacher swipes
  # (card 6aa8b5d750645cd1c3e765ab) without remounting itself.
  params:
    studentId:
      dynamic: true
  onEnter: requireLogin
  component: Dashboard
  resolve:[
    token: "initialized",
    resolveFn: ->
      console.dir window.app
      window.app.trackPageView("2: Dashboard")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'games'
  url:'/games?studentId'
  onEnter: requireLogin
  component: GamesList
  resolve:[
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.refresh() if window.app.needsRefresh()
      window.app.trackPageView("2: Game List")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)

  ]
,
  name: 'subjects'
  url:'/subjects?studentId'
  redirectTo: requireLogin
  component: SubjectList
  resolve:[
    token: 'subjects'
    resolveFn: ->
      window.app.trackPageView("2: Subject List")
  ,
    token: 'refresh'
    resolveFn: ->
      if window.app.needsRefresh()
        student = window.app.getActiveStudent()
        session_duration = if student.session_duration? then student.session_duration else null
        p = window.app.refresh().then ->
          window.app.getActiveStudent().session_duration = session_duration if session_duration?
        return p
  ]
,
  name: 'student_report'
  url: '/student_report?studentId'
  params: {studentIds: {array: true, type: 'string', value: null}}
  redirectTo: requireLogin
  component: StudentReport
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Student Report")
      if window.app.needsRefresh() then return window.app.refresh() else return null
  ]
,
  name: 'cancel'
  url: '/cancel'
  redirectTo: requireLogin
  component: Cancel
  resolve: [
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Cancel")
  ]
,
  name: 'play'
  url:'/play/:gameId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete'
  onEnter: requireLogin
  component: GameLayout
  resolve:[
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game Play")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ]
,
  name: 'playSubject'
  url:'/playsubject/:subjectId/:studentId?nodeId&activityId&successAnimationId&closeOnComplete'
  redirectTo: requireLogin
  component: GameLayout
  resolve:[
    token: 'studentRefresh',
    resolveFn: ({studentId}) ->
      console.log "Refreshing student #{studentId}..."
      window.app.loopbackAPI.Student.findById({id: studentId}, {include: {relation: 'posetProfiles', scope: {include: {relation: 'poset', scope: {fields: ['name']}}}}}).then (response) ->
        student = window.app.getStudentById(studentId)
        if student
          student.posetProfiles = response.data.posetProfiles
          window.app.dirty = true
    deps: ['$stateParams']
  ,
    token: 'games'
    deps: ['$stateParams']
    resolveFn: ({studentId}) ->
      window.app.trackPageView("2: Game Play")
      # studentId comes from $stateParams, NOT from the active student: this
      # resolve runs BEFORE digestParams latches it, so reading the active
      # student here filters the game voices for the child being navigated
      # away from (card 6aa4313530706c48305d2195).
      window.app.loadGames(studentId)
  ]
,
  name: 'tokens'
  url:'/tokens'
  onEnter: requireLogin
  component: Tokens
  resolve:[
    token: 'initialized'
    resolveFn: ->
      window.app.trackPageView("2: Login Tokens")
  ]
]

# The query can sit before the fragment (/org?access_token=..&uid=..#/dashboard)
# or inside it (/app#/login?access_token=.., GET /login's redirect), so cut the
# fragment off what follows the last '?', never off the URL: a uid that
# swallowed '#/dashboard' installed a session for no login (card 6aa6dbae617b8dc32cc6b4c0).
parseUrl = ( url = location.href ) ->
  params = {}
  ( ( parts = part.split( "=" ) ) && params[ parts[0] ] = parts[1] for part in ( url.split "?" ).pop().split( "#" )[0].split "&" if url.indexOf( "?" ) != -1 ) && params || {}

params = parseUrl window.location.href

clientAPI = new ClientAPI(window.config.basePath || '', "/clientapi", "$teacher$")
# Pre-set auth from URL params before loading schema.
# Without this, arriving at /teacher with no $teacher$ token in localStorage
# causes loadSchema() to run unauthenticated, returning a partial schema that
# is missing teacher-specific models and methods.
if params.access_token? && !clientAPI.isLoggedIn()
  clientAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
  clientAPI.resetAuthHeader()
clientAPI.loadSchema().then () ->
  window.app = new Application(clientAPI)

  # Make i18n functions globally available for lazy-loaded chunks
  window.i18nCreateElement = i18nCreateElement
  window.t = t

  hide_agent_widget() if window.app.isApp()

  configure = (router) ->
    router.urlRouter.otherwise {state: 'teacherDashboard'}

    # Update i18n context on route transitions
    router.transitionService.onSuccess {}, (transition) ->
      toState = transition.to()
      # Strip query parameters from route for cleaner tracking
      routeWithoutQuery = window.location.hash.split('?')[0]
      normalizedRoute = normalizeRoute(routeWithoutQuery)
      window.i18nRegistry?.setContext(
        "elfeet/teacher/#{toState.name}",
        normalizedRoute,
        'elfeet'
      )

  start_react = ->
    root = createRoot(document.getElementById("root"))
    root.render(
      <ErrorBoundary>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <TranslationProvider route={window.location.hash} app="elfeet">
            <UIRouter plugins={[hashLocationPlugin]} states={globalStates} config={configure}>
              <div style={{height: "100%"}}>
                <GlobalToast/>
                <UIView/>
              </div>
            </UIRouter>
          </TranslationProvider>
        </ThemeProvider>
      </ErrorBoundary>
    )

  initialize = (route_to='teacher_dashboard', route_params={}) ->
    window.application_running = 'teacher'
    window.app.refresh().then (response) ->
      if response.data?.login? # we did not fetch account data, send them to login screen...
        # Initialize i18n with user's preferred language
        preferredLang = response.data.login.preferredLanguage || response.data.login.preferred_language || 'en'
        # console.log "Initializing i18n with preferred language: #{preferredLang}"
        initI18n(clientAPI, preferredLang)

        # Set moment locale for date formatting and month names
        if preferredLang and !preferredLang.startsWith('en')
          momentLang = preferredLang.split('-')[0].toLowerCase()
          import("moment/locale/#{momentLang}")
            .then -> moment.locale(momentLang)
            .catch -> console.warn "moment locale #{momentLang} not available"

        # Fetch all translations before navigating so first render is already translated
        # 'en-US' is not 'en': ask the framework whether this locale reads the
        # authored text before fetching the catalogue (card 6aa6c73847c945dd8596e7d9).
        if !window.i18nManager.readsSource()
          await clientAPI.i18nString.find({where: {app: 'elfeet'}}).then (resp) ->
            cache = {}
            strings = if Array.isArray(resp) then resp else (resp?.data || [])
            for string in strings
              cache[string.hash] = string.text
            try
              localStorage.setItem("i18n_#{preferredLang}_elfeet", JSON.stringify(cache))
              window.i18nTranslationCache ?= {}
              for hash, text of cache
                window.i18nTranslationCache[hash] = text
            catch e
              console.warn "Failed to cache teacher translations:", e

        # Initialize i18n registry
        window.i18nRegistry = new I18nRegistry(clientAPI)
        # Strip query parameters from route for cleaner tracking
        routeWithoutQuery = window.location.hash.split('?')[0]
        normalizedRoute = normalizeRoute(routeWithoutQuery)
        window.i18nRegistry.setContext('elfeet/teacher/init', normalizedRoute, 'elfeet')

        # Make i18n utilities globally available
        window.i18nCreateElement = i18nCreateElement
        window.t = t

        if moment(response.data.login.account.subscription_end).isAfter(moment())
          if window.app.account.force_onboarding
            window.location = "#/onboarding"
          else
            locString = "#/#{route_to}"
            c = '?'
            for key, value of route_params
              locString += "#{c}#{key}=#{value}"
              c = '&'
            window.location = locString      
        else if moment(response.data.login.account.subscription_end).isBefore(moment())
          if window.app.account.force_onboarding
            window.location = "#/onboarding"
          else
            window.location = '#/account?tab=2'
        else
          window.location.hash = ''
        start_react()
      else
        window.location.hash = ''

  window.teacher_mode = true
  window.TitleBar = TitleBar
  if params.access_token? && params.uid? && window.app.loopbackAPI.isLoggedIn()
    window.app.loopbackAPI.clearUser() if window.app.loopbackAPI.currentUserId != params.uid
    window.history.replaceState {}, document.title, "/teacher"
    
  if window.app.loopbackAPI.isLoggedIn() && !(params.new_user == 'true' && params.access_token?)
    if !window.location.hash? || window.location.hash is '' || window.location.hash is '#/'
      initialize()
    else
      route = window.location.hash.substring 2
      initialize route
  else if params.access_token?
    window.app.loopbackAPI.setUser(params.access_token, params.uid, undefined, params.studentId)
    window.app.loopbackAPI.resetAuthHeader()
    window.history.replaceState {}, document.title, "/teacher"

    # to avoid variable capture, redirectParams are passed as a separate hash
    redirectParams = {}
    try
      if params.redirectParams?
        redirectParams = JSON.parse(params.redirectParams)
    catch
      console.error "Ignoring bad redirect params: #{params.redirectParams}"

    initialize params.redirectTo, params.redirectParams
  else if window.location.hash != '' and not isUnauthenticatedDeepLink(window.location.hash)
    # A logged-out visitor normally gets dumped at the sign-in screen, so any
    # stale hash is wiped here before the router starts. The reset link is the
    # one deep link that MUST survive that: its whole audience is people who
    # cannot sign in, arriving cold from an email. Blanking the hash here threw
    # the token away before UI-Router ever saw it, and `otherwise` then sent
    # them to the dashboard and on to login — so the emailed link could never
    # work for the only people it is for. Card 6a639e22893d0bbbaaacec81.
    window.location.hash = ''
    start_react()
  else
    start_react()
